import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';
import { SITE } from '@/constants/site';
import { milestones, stats } from '@/mocks/content';
import { Link } from 'react-router-dom';

const principles = [
  {
    icon: 'ri-separator',
    title: '区分事实与观点',
    text: '已确认的信息、当事方的公开回应、网友的判断，三类内容分开呈现，不混为一谈。',
  },
  {
    icon: 'ri-loader-4-line',
    title: '标注信息状态',
    text: '「讨论中」「等待回应」「已有回应」「事件已澄清」四个状态，只描述当前信息是否完整。',
  },
  {
    icon: 'ri-shield-user-line',
    title: '不下结论',
    text: '我们整理线索，不代替读者判断，也不替任何当事人定性。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新',
    text: '话题页会随着新信息补充时间线，错误的整理会被明确更正，而不是悄悄删掉。',
  },
];

export default function About() {
  useSeo({
    title: '关于我们 - 平台定位与内容原则',
    description:
      '了解瓜田观察的定位、内容原则与发展历程。我们整理热点事件的时间线与各方说法，标注信息状态，不替当事人下结论，让每一次讨论都有据可依。',
    keywords: '关于我们,内容原则,信息状态,时间线整理,平台定位,瓜田观察,隐私与版权',
    path: '/about',
    type: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'AboutPage',
          name: '关于我们',
          description: '瓜田观察的平台定位、内容原则与发展历程。',
          inLanguage: 'zh-CN',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: `${window.location.origin}/about` },
          ],
        },
      ],
    },
  });

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: '关于我们' }]} />
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-black leading-snug text-foreground-950 md:text-4xl">
            我们把「吃瓜」当成一件需要认真做的事
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {SITE.name}是一个围绕热点讨论的内容社区。我们相信，热度会过去，但对事情的理解会留下——所以每个话题都值得被整理得清楚一点。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-4 py-14 md:px-8 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div className="h-64 w-full overflow-hidden bg-background-200 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20minimal%20editorial%20desk%20with%20notebook%2C%20timeline%20sketch%20and%20coffee%20cup%20in%20soft%20morning%20light%2C%20cinematic%20photography%2C%20amber%20tones%2C%20clean%20composition&width=900&height=720&seq=gt-about-01&orientation=landscape"
                alt="编辑整理话题时间线的工作场景示意图"
                title="瓜田观察 平台定位与内容整理"
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-primary-600">What We Do</p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们的定位
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                网上关于同一个热点的信息往往散落在无数条动态里：有人发片段，有人贴截图，有人急着下结论。
                我们做的事情很朴素——把能找到的公开信息按时间排好，把各方说法并列，再交给读者自己判断。
              </p>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                我们不生产指控，也不为任何一方站台。话题页里出现的「讨论」，都来自网友的观点整理；出现在「各方说法」里的内容，都会标注来源与状态。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="w-full px-4 py-14 md:px-8 md:py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-primary-600">Principles</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">四条内容原则</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item) => (
              <div key={item.title} className="border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center bg-primary-100 text-primary-600">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-4 py-14 md:px-8 md:py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-primary-600">Our Story</p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展历程</h2>
          <ol className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {milestones.map((item) => (
              <li key={item.year} className="border-t-2 border-primary-500 bg-background-100 p-6">
                <span className="font-heading text-2xl font-black text-primary-500">{item.year}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-secondary-900">
        <div className="w-full px-4 py-12 md:px-8 md:py-14">
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-secondary-800 text-accent-400">
                  <i className={`${stat.icon} text-lg`} aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-heading text-xl font-bold text-background-50 md:text-2xl">{stat.value}</span>
                  <span className="mt-1 block text-xs text-secondary-300">{stat.label}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-4 py-14 md:px-8 md:py-16">
          <div className="flex flex-col items-start justify-between gap-6 border border-background-200 bg-background-100 p-8 md:flex-row md:items-center">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950">想和我们聊聊？</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                线索投稿、内容纠错、隐私处理或转载沟通，都可以直接联系我们。
              </p>
              <p className="mt-3 text-sm text-foreground-600">
                <i className="ri-mail-line mr-2 text-primary-500" aria-hidden="true" />
                <a href={`mailto:${SITE.email}`} className="text-primary-600 underline underline-offset-4">
                  {SITE.email}
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="whitespace-nowrap bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              前往联系我们
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}